import { FlashNoticeVariant } from 'components/FlashNotice'
import { showFlashNotice } from 'components/FlashNotice/duck/actions'
import { callApi, waitFor } from 'duck/operations'
import { apiGetFundingEventAsAdmin } from 'entities-next/fundingEvents/fundingEvent.api'
import { differenceBy, isNil } from 'lodash'
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import { ForbiddenError } from 'services/api'
import { ActionType, getType } from 'typesafe-actions'

import delay from '@redux-saga/delay-p'

import { apiDeleteDigitalPresenceAsAdmin, apiPostDigitalPresenceAsAdmin } from '../digitalPresences/api'
import { DigitalPresence, DigitalPresenceType } from '../digitalPresences/types'
import { FundingEvent } from '../fundingEvents/types'
import { apiGetAllFundingRoundsAsAdmin, apiGetAllFundingRoundsAsCurrentUser } from '../fundingRounds/api'
import { FundingRound } from '../fundingRounds/types'
import {
  apiDeleteSignalAssessmentAsAdmin,
  apiPostSignalAssessmentAsAdmin,
  apiPutSignalAssessmentAsAdmin,
} from '../signalAssessments/api'
import { SignalAssessment } from '../signalAssessments/types'
import {
  createOrganization,
  createOrganizationMatch,
  deleteOrganizationContent,
  fetchAllOrganizations,
  fetchOneOrganization,
  releaseOrganizationMatches,
  updateOrganization,
} from './actions'
import {
  apiCreateOrganizationMatch,
  apiDeleteOrganization,
  apiGetAllOrganizations,
  apiGetOrganization,
  apiGetOrganizationAsAdmin,
  apiGetSetOfOrganizationsFromManager,
  apiPostOrganizationAsAdmin,
  apiPutOrganizationAsAdmin,
  apiReleaseOrganizationMatches,
} from './api'
import { getOrganization } from './selectors'
import { IdIndexedOrganizations, Organization, OrganizationData } from './types'

export function* organizationsSaga() {
  yield all([
    takeLatest(getType(fetchAllOrganizations.request), fetchAllOrganizationsSaga),
    takeEvery(getType(fetchOneOrganization.request), fetchOrganizationAsUser),
    takeEvery(getType(createOrganization.request), postNewOrganization),
    takeEvery(getType(createOrganization.success), waitAndFetchOrganization),
    takeEvery(getType(createOrganization.success), showCreateOrganizationSuccessFlashNotice),
    takeEvery(getType(createOrganization.failure), showCreateOrganizationFailureFlashNotice),
    takeEvery(getType(updateOrganization.request), updateOrganizationSaga),
    takeEvery(getType(updateOrganization.success), showUpdateOrganizationSuccessFlashNotice),
    takeEvery(getType(deleteOrganizationContent.request), deleteOrganizationContentSaga),
    takeEvery(getType(createOrganizationMatch.request), createOrganizationMatchSaga),
    takeEvery(getType(createOrganizationMatch.success), showSuccessfulMatch),
    takeEvery(getType(releaseOrganizationMatches.request), releaseOrganizationMatchesEffect),
  ])
}

function* fetchAllOrganizationsSaga(action: ActionType<typeof fetchAllOrganizations.request>) {
  const { currentPage, limit, searchQuery } = action.payload
  const organizations: IdIndexedOrganizations = yield callApi(apiGetAllOrganizations, currentPage, limit, searchQuery)

  yield put(fetchAllOrganizations.success(organizations))
}

function* waitAndFetchOrganization(action: ActionType<typeof createOrganization.success>) {
  const organization = action.payload
  yield call(delay, 3000)
  yield fetchOrganizationAsAdmin(organization.id)
}

function* fetchOrganizationAsUser(action: ActionType<typeof fetchOneOrganization.request>) {
  const organizationId = action.payload
  const organizationData: Organization = yield callApi(apiGetOrganization, organizationId)
  const fundingRounds: FundingRound[] = yield callApi(apiGetAllFundingRoundsAsCurrentUser, organizationId)
  const fundingEvent: FundingEvent = yield callApi(apiGetFundingEventAsAdmin, organizationId)
  const organization = {
    ...organizationData,
    fundingRounds,
    fundingEvent,
  }
  yield put(fetchOneOrganization.success(organization))
}

function* fetchOrganizationAsAdmin(organizationId: number) {
  const organizationData: Organization = yield callApi(apiGetOrganizationAsAdmin, organizationId)
  const fundingRounds: FundingRound[] = yield callApi(apiGetAllFundingRoundsAsAdmin, organizationId)
  const fundingEvent: FundingEvent = yield callApi(apiGetFundingEventAsAdmin, organizationId)
  const organization = {
    ...organizationData,
    fundingRounds,
    fundingEvent,
  }
  yield put(fetchOneOrganization.success(organization))
}

export function* waitForFetchOneOrganizationSuccess(organizationId: Organization['id']) {
  yield call(
    waitFor,
    fetchOneOrganization.success,
    (organization: ActionType<typeof fetchOneOrganization.success>['payload']) => organization.id === organizationId
  )
}

function* postNewOrganization(action: ActionType<typeof createOrganization.request>) {
  const { organization, isCreated } = action.payload

  try {
    const newOrganization: Organization = yield callApi(apiPostOrganizationAsAdmin, organization, isCreated)
    yield put(createOrganization.success(newOrganization))
  } catch (error) {
    if (error instanceof ForbiddenError) {
      yield put(
        createOrganization.failure({
          organizationLocator: organization,
          error: 'Organization already exists',
          isDuplicated: true,
        })
      )
    } else {
      yield put(
        createOrganization.failure({
          organizationLocator: organization,
          error: error.message,
        })
      )
    }
  }
}

function* updateDocumentalDigitalPresences(organization: OrganizationData): any {
  const { digitalPresence: digitalPresences } = organization

  if (!digitalPresences || !digitalPresences.length) {
    return []
  }

  const apiCalls = digitalPresences.reduce((acc: any, digitalPresence: DigitalPresence) => {
    if (digitalPresence.type !== DigitalPresenceType.documentUrl) {
      return acc
    }

    if (isNil(digitalPresence.id)) {
      acc.push(callApi(apiPostDigitalPresenceAsAdmin, digitalPresence, organization.id))
    } else if (digitalPresence.toDelete) {
      acc.push(callApi(apiDeleteDigitalPresenceAsAdmin, digitalPresence))
    }

    return acc
  }, [])

  return yield all(apiCalls)
}

function* updateSignalAssessments(organizationId: number, signalAssessments: SignalAssessment[] = []): any {
  const { signalAssessments: oldSignalAssessments = [] }: Organization = yield select(getOrganization, organizationId)
  const deleteApiCalls = differenceBy(oldSignalAssessments, signalAssessments, 'id').map((signalAssessment) =>
    callApi(apiDeleteSignalAssessmentAsAdmin, signalAssessment)
  )
  const updateOrCreateApiCalls = signalAssessments.map((signalAssessment) => {
    if (!!signalAssessment.id) {
      return callApi(apiPutSignalAssessmentAsAdmin, signalAssessment)
    } else {
      return callApi(apiPostSignalAssessmentAsAdmin, signalAssessment)
    }
  })

  return yield all([...deleteApiCalls, ...updateOrCreateApiCalls])
}

export function* updateOrganizationSaga({ payload }: ActionType<typeof updateOrganization.request>) {
  const { organization } = payload
  const { fundingRounds, signalAssessments, ...organizationData } = organization

  try {
    const nonDocumentDigitalPresences = organization.digitalPresence
      ? organization.digitalPresence.filter((dp: any) => dp.type !== DigitalPresenceType.documentUrl)
      : undefined

    yield updateDocumentalDigitalPresences(organization)
    yield updateSignalAssessments(organization.id, signalAssessments)

    const updatedOrganizationData: Organization = yield callApi(apiPutOrganizationAsAdmin, {
      ...organizationData,
      digitalPresence: nonDocumentDigitalPresences,
    })

    yield put(updateOrganization.success(updatedOrganizationData))
    yield put(fetchOneOrganization.success(updatedOrganizationData))
  } catch (error) {
    yield put(updateOrganization.failure(error))
  }
}

function* deleteOrganizationContentSaga(action: ActionType<typeof deleteOrganizationContent.request>) {
  const { organizationId } = action.payload
  yield callApi(apiDeleteOrganization, organizationId)
  const organizations: IdIndexedOrganizations = yield callApi(apiGetSetOfOrganizationsFromManager, [organizationId])
  yield put(deleteOrganizationContent.success(organizations[organizationId]))
}

function* createOrganizationMatchSaga(action: ActionType<typeof createOrganizationMatch.request>) {
  const { organization, matchFilter } = action.payload

  yield callApi(apiCreateOrganizationMatch, organization.id, matchFilter)
  yield put(createOrganizationMatch.success(organization))
}

function* releaseOrganizationMatchesEffect(action: ActionType<typeof releaseOrganizationMatches.request>) {
  const { organization } = action.payload
  const { name } = organization
  try {
    yield callApi(apiReleaseOrganizationMatches, organization.id)
    yield put(releaseOrganizationMatches.success())
    yield put(
      showFlashNotice({
        message: `Matches release for "${name}" successful!`,
        autoHide: true,
        variant: FlashNoticeVariant.success,
      })
    )
  } catch (e) {
    yield put(releaseOrganizationMatches.failure(e))

    if (e instanceof ForbiddenError) {
      yield put(
        showFlashNotice({
          message: e.message,
          autoHide: true,
          variant: FlashNoticeVariant.error,
        })
      )
    } else {
      yield put(
        showFlashNotice({
          message: e.message || `Matches release for "${name}" failed!`,
          autoHide: true,
          variant: FlashNoticeVariant.error,
        })
      )
    }
  }
}

function* showSuccessfulMatch({ payload }: ActionType<typeof createOrganizationMatch.success>) {
  const { name } = payload
  yield put(
    showFlashNotice({
      message: `"${name}" matching process with users successfully launched!`,
      autoHide: true,
      variant: FlashNoticeVariant.success,
    })
  )
}

function* showUpdateOrganizationSuccessFlashNotice({ payload }: ActionType<typeof updateOrganization.success>) {
  const { name } = payload
  yield put(
    showFlashNotice({
      message: `Organization "${name}" successfully updated!`,
      autoHide: true,
      variant: FlashNoticeVariant.success,
    })
  )
}

function* showCreateOrganizationSuccessFlashNotice(action: ActionType<typeof createOrganization.success>) {
  const organization = action.payload
  yield put(
    showFlashNotice({
      message: `Organization "${organization.name}" successfully created!`,
      autoHide: true,
      variant: FlashNoticeVariant.success,
    })
  )
}

function* showCreateOrganizationFailureFlashNotice(action: ActionType<typeof createOrganization.failure>) {
  const { organizationLocator, isDuplicated } = action.payload

  if (isDuplicated) {
    yield put(
      showFlashNotice({
        message: `Organization "${organizationLocator.name}" already exists.`,
        autoHide: false,
        variant: FlashNoticeVariant.warning,
      })
    )
  } else {
    yield put(
      showFlashNotice({
        message: `Error while creating organization "${organizationLocator.name}"`,
        autoHide: false,
        variant: FlashNoticeVariant.error,
      })
    )
  }
}
